import React from 'react';
import { useTheme } from '../lib/theme';

export function Badge({ children, color, bg }) {
  color = color || '#E8A838';
  return React.createElement('span', { style: {
    display: 'inline-block', padding: '3px 10px', borderRadius: 4,
    fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
    color: color, background: bg || color + '18', border: '1px solid ' + color + '30',
    fontFamily: "'DM Mono', monospace", whiteSpace: 'nowrap',
  }}, children);
}

export function Card({ children, style, glow }) {
  var t = useTheme();
  return React.createElement('div', { style: Object.assign({
    background: t.c.bgCard, border: '1px solid ' + t.c.borderCard,
    borderRadius: 10, padding: 20, position: 'relative', overflow: 'hidden',
  }, glow ? { boxShadow: '0 0 30px -10px ' + glow + '40' } : {}, style || {}) }, children);
}

export function SectionTitle({ children }) {
  var t = useTheme();
  return React.createElement('h3', { style: {
    fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.14em',
    color: t.c.textFaint, margin: '0 0 14px 0', fontFamily: "'DM Mono', monospace",
  }}, children);
}

export function MetricRow({ label, value, color }) {
  var t = useTheme();
  return React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid ' + t.c.borderCard } },
    React.createElement('span', { style: { fontSize: 12, color: t.c.textMuted } }, label),
    React.createElement('span', { style: { fontSize: 12, fontWeight: 600, color: color || t.c.text, fontFamily: "'DM Mono', monospace", textAlign: 'right', maxWidth: '60%', lineHeight: 1.4 } }, value)
  );
}

export function GaugeRing({ value, max, size, stroke, color, label, sublabel }) {
  max = max || 100; size = size || 88; stroke = stroke || 7;
  var r = (size - stroke) / 2;
  var circ = 2 * Math.PI * r;
  var pct = Math.min(typeof value === 'number' ? value / max : 0, 1);
  var display = max === 1 ? Math.round(pct * 100) + '%' : Math.round(value);
  return React.createElement('div', { style: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 } },
    React.createElement('svg', { width: size, height: size, viewBox: '0 0 ' + size + ' ' + size },
      React.createElement('circle', { cx: size/2, cy: size/2, r: r, fill: 'none', stroke: 'rgba(255,255,255,0.06)', strokeWidth: stroke }),
      React.createElement('circle', { cx: size/2, cy: size/2, r: r, fill: 'none', stroke: color, strokeWidth: stroke,
        strokeDasharray: circ, strokeDashoffset: circ * (1 - pct), strokeLinecap: 'round',
        transform: 'rotate(-90 ' + size/2 + ' ' + size/2 + ')', style: { transition: 'stroke-dashoffset 1s ease' } }),
      React.createElement('text', { x: size/2, y: size/2 - (sublabel ? 2 : 0), textAnchor: 'middle', dominantBaseline: 'central',
        fill: '#fff', fontSize: size > 80 ? 20 : 16, fontWeight: 700, fontFamily: "'DM Sans', sans-serif" }, display),
      sublabel && React.createElement('text', { x: size/2, y: size/2 + 14, textAnchor: 'middle', fill: 'rgba(255,255,255,0.4)', fontSize: 9, fontFamily: "'DM Sans', sans-serif" }, sublabel)
    ),
    React.createElement('span', { style: { fontSize: 10, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.08em', textAlign: 'center', lineHeight: 1.2 } }, label)
  );
}

export function MiniGauge({ value, color, size }) {
  size = size || 36; var r = 14; var circ = 2 * Math.PI * r; var pct = Math.min(value, 1);
  return React.createElement('svg', { width: size, height: size, viewBox: '0 0 ' + size + ' ' + size },
    React.createElement('circle', { cx: size/2, cy: size/2, r: r, fill: 'none', stroke: 'rgba(255,255,255,0.06)', strokeWidth: 3 }),
    React.createElement('circle', { cx: size/2, cy: size/2, r: r, fill: 'none', stroke: color, strokeWidth: 3,
      strokeDasharray: circ, strokeDashoffset: circ * (1 - pct), strokeLinecap: 'round',
      transform: 'rotate(-90 ' + size/2 + ' ' + size/2 + ')' }),
    React.createElement('text', { x: size/2, y: size/2 + 1, textAnchor: 'middle', dominantBaseline: 'central',
      fill: '#fff', fontSize: 9, fontWeight: 700, fontFamily: "'DM Mono', monospace" }, Math.round(value * 100))
  );
}

export function OutcomeBadge({ showed, closed, dealAmount }) {
  if (showed === 'TRUE' && closed === 'TRUE') {
    return React.createElement(Badge, { color: '#4ADE80' }, 'CLOSED' + (dealAmount ? ' $' + Number(dealAmount).toLocaleString() : ''));
  }
  if (showed === 'TRUE' && closed === 'FALSE') {
    return React.createElement(Badge, { color: '#E8A838' }, 'SHOWED \u2022 NO CLOSE');
  }
  if (showed === 'FALSE') {
    return React.createElement(Badge, { color: '#EF4444' }, 'NO SHOW');
  }
  if (showed === 'RESCHEDULED') {
    return React.createElement(Badge, { color: '#38BDF8' }, 'RESCHEDULED');
  }
  if (closed === 'PENDING' || showed === 'PENDING') {
    return React.createElement(Badge, { color: 'rgba(255,255,255,0.3)' }, 'PENDING');
  }
  return null;
}

export function TimingBadge({ rating }) {
  if (!rating) return null;
  var colors = {
    instant: '#4ADE80', fast: '#4ADE80', immediate: '#4ADE80',
    acceptable: '#E8A838', moderate: '#E8A838', standard: '#E8A838', warm: '#E8A838', tight: '#38BDF8',
    slow: '#EF4444', very_slow: '#EF4444', critical_delay: '#EF4444', excessive: '#EF4444', cold: '#EF4444', frozen: '#EF4444',
    no_response: '#EF4444', fresh: '#4ADE80', cooling: '#E8A838', extended: '#E8A838',
  };
  var c = colors[rating] || 'rgba(255,255,255,0.3)';
  return React.createElement(Badge, { color: c }, rating.replace(/_/g, ' '));
}

export function ConfBar({ label, value }) {
  var c = value >= 0.7 ? '#4ADE80' : value >= 0.5 ? '#E8A838' : '#EF4444';
  return React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0' } },
    React.createElement('span', { style: { fontSize: 10, color: 'rgba(255,255,255,0.4)', width: 80, flexShrink: 0, textTransform: 'uppercase', letterSpacing: '0.05em' } }, label),
    React.createElement('div', { style: { flex: 1, height: 4, background: 'rgba(255,255,255,0.06)', borderRadius: 2, overflow: 'hidden' } },
      React.createElement('div', { style: { width: (value * 100) + '%', height: '100%', background: c, borderRadius: 2, transition: 'width 0.8s ease' } })
    ),
    React.createElement('span', { style: { fontSize: 10, color: c, fontFamily: "'DM Mono', monospace", width: 28, textAlign: 'right' } }, Math.round(value * 100) + '%')
  );
}

export var BAND_COLORS = { WEAK_CURIOSITY: '#EF4444', PASSIVE_INTEREST: '#E8A838', ENGAGED: '#38BDF8', COMMITTED: '#4ADE80' };
export var ARCHETYPE_COLORS = { locked_in: '#4ADE80', slow_burn: '#38BDF8', polite_ghost: '#EF4444', fence_sitter: '#E8A838', rescheduler: '#F472B6', obligation_attender: '#818CF8', early_dropout: '#EF4444', external_dependency: '#E8A838' };
export var URGENCY_COLORS = { immediate: '#EF4444', within_24h: '#E8A838', within_48h: '#38BDF8' };
